// Single source of truth for resolving timeline state at a given playhead time.
// Both the live preview compositor (VideoPlayer) and the timeline UI read from here so
// the preview always mirrors the edited timeline (same interpretation the FFmpeg export uses).

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Turn a stored asset URL ("/api/media?path=...") into an absolute URL the browser can load.
export function absUrl(u) {
  if (!u) return u;
  if (u.startsWith("blob:") || u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${BACKEND}${u}`;
  return u;
}

export function videoClips(mediaTracks) {
  const out = [];
  (mediaTracks || []).forEach((tr) => {
    if (tr.type === "video") (tr.objects || []).forEach((o) => out.push({ tr, o }));
  });
  out.sort((a, b) => (a.o.start || 0) - (b.o.start || 0));
  return out;
}

export function imageClips(mediaTracks) {
  const out = [];
  (mediaTracks || []).forEach((tr) => {
    (tr.objects || []).forEach((o) => { if (o.kind === "image") out.push({ tr, o }); });
  });
  out.sort((a, b) => (a.o.start || 0) - (b.o.start || 0));
  return out;
}

// The active video clip at time t. Clips are drawn in start order in the export compositor,
// so the LAST-matching active clip is the top-most one — mirror that here.
export function resolveActiveVideoClip(mediaTracks, t) {
  const clips = videoClips(mediaTracks);
  let active = null;
  for (const c of clips) {
    if (c.tr.hidden || c.o.hidden) continue;
    const s = c.o.start || 0, e = c.o.end ?? (s + 1);
    if (t >= s - 1e-4 && t < e - 1e-4) active = c;
  }
  return active;
}

export function resolveActiveImages(mediaTracks, t) {
  return imageClips(mediaTracks).filter(({ tr, o }) => {
    if (tr.hidden || o.hidden) return false;
    const s = o.start || 0, e = o.end ?? (s + 5);
    return t >= s - 1e-4 && t < e - 1e-4;
  });
}

export function elementClips(mediaTracks) {
  const out = [];
  (mediaTracks || []).forEach((tr) => {
    (tr.objects || []).forEach((o) => { if (o.kind === "element") out.push({ tr, o }); });
  });
  out.sort((a, b) => (a.o.start || 0) - (b.o.start || 0));
  return out;
}

export function resolveActiveElements(mediaTracks, t) {
  return elementClips(mediaTracks).filter(({ tr, o }) => {
    if (tr.hidden || o.hidden) return false;
    const s = o.start || 0, e = o.end ?? (s + 4);
    return t >= s - 1e-4 && t < e - 1e-4;
  });
}

// Source-media time for a timeline time: sourceStart + (t - timelineStart).
export function sourceTimeFor(clip, t) {
  const ss = clip.source_start || 0;
  return ss + Math.max(0, (t - (clip.start || 0)));
}

// The decode source URL for a clip: an added clip carries its own asset_url; the base clip
// (no asset) uses the project's main video URL.
export function clipSourceUrl(clip, baseUrl) {
  return absUrl(clip && clip.asset_url) || baseUrl;
}

export function timelineDuration(mediaTracks, captionLayers, fallback = 0) {
  let d = 0, hasMedia = false;
  (mediaTracks || []).forEach((tr) => (tr.objects || []).forEach((o) => { hasMedia = true; d = Math.max(d, o.end || 0); }));
  (captionLayers || []).forEach((l) => (l.objects || []).forEach((o) => { d = Math.max(d, o.end || 0); }));
  if (!hasMedia) d = Math.max(d, fallback);
  return d > 0 ? d : fallback;
}

// Audio clips that carry their OWN source (music / voiceover uploads). The base video's own
// audio is played by its video element, so we exclude linked clips (no asset_url) to avoid
// double audio.
export function audioClips(mediaTracks) {
  const out = [];
  (mediaTracks || []).forEach((tr) => {
    if (tr.type === "audio") (tr.objects || []).forEach((o) => { if (o.asset_url) out.push({ tr, o }); });
  });
  return out;
}

// The video clip immediately preceding `clipObj` on the timeline (for crossfade).
export function prevVideoClip(mediaTracks, clipObj) {
  let best = null;
  videoClips(mediaTracks).forEach((c) => {
    if (c.o === clipObj || c.tr.hidden || c.o.hidden) return;
    if ((c.o.end || 0) <= (clipObj.start || 0) + 0.06) {
      if (!best || (c.o.end || 0) > (best.o.end || 0)) best = c;
    }
  });
  return best;
}

// Normalised crop {l,t,r,b} (fractions of the source frame) or null when uncropped.
export function clipCrop(o) {
  const c = o && o.crop;
  if (!c) return null;
  const f = (v) => Math.max(0, Math.min(0.9, +v || 0));
  const l = f(c.l), t = f(c.t), r = f(c.r), b = f(c.b);
  if (!(l || t || r || b) || l + r > 0.92 || t + b > 0.92) return null;
  return { l, t, r, b };
}
// Source rect [sx, sy, sw, sh] for drawImage after cropping.
export const cropRect = (crop, vw, vh) => (crop
  ? [crop.l * vw, crop.t * vh, vw * (1 - crop.l - crop.r), vh * (1 - crop.t - crop.b)] : [0, 0, vw, vh]);
export const clipOpacity = (o) => Math.max(0, Math.min(1, o && o.opacity != null ? +o.opacity : 1));

// CSS filter string mirrored by the FFmpeg export (brightness/contrast/blur/grayscale).
export function cssFilter(f) {
  if (!f) return "none";
  const parts = [];
  if (f.brightness != null && +f.brightness !== 1) parts.push(`brightness(${f.brightness})`);
  if (f.contrast != null && +f.contrast !== 1) parts.push(`contrast(${f.contrast})`);
  if (f.blur) parts.push(`blur(${f.blur}px)`);
  if (f.grayscale) parts.push("grayscale(1)");
  return parts.length ? parts.join(" ") : "none";
}
